import { useState } from "react";
import { useRevalidator } from "react-router-dom";

import {
  getListings,
  getListingsById,
  getMemberships,
  getMembershipsByMembershipId,
  getMembershipsDiscountPrices,
  getPlatformLocation,
  getPlatformLocations,
  getPromotions,
  getPromotionsActiveBanners,
  getRetailproductsByPlatformIdProductListings,
  getShop,
  getShopCustomCategoriesListings,
  getShopMemberPricing,
  getUser,
  postUserUpdate,
} from "api/methods";
import { invalidateShopDiscounts } from "modules/cart/useCartDetails";
import { queryClient } from "pages/Root";

/**
 * Everything the shop reads that is scoped to the patient's location. Switching location without
 * clearing these leaves the previous location's catalog on screen.
 */
export const invalidateShopForLocationChange = () =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: getUser.key() }),
    queryClient.invalidateQueries({ queryKey: getListings.key() }),
    queryClient.invalidateQueries({ queryKey: getShop.key() }),
    queryClient.invalidateQueries({ queryKey: getShopCustomCategoriesListings.key() }),
    invalidateShopDiscounts(),
    queryClient.invalidateQueries({ queryKey: getPromotions.key() }),
    queryClient.invalidateQueries({ queryKey: getPromotionsActiveBanners.key() }),
    queryClient.invalidateQueries({ queryKey: getMemberships.key() }),
    // Who prices a non-member or visitor, and the retail member prices, follow the location.
    queryClient.invalidateQueries({ queryKey: getShopMemberPricing.key() }),
    queryClient.invalidateQueries({ queryKey: getPlatformLocation.key() }),
    queryClient.invalidateQueries({ queryKey: getMembershipsDiscountPrices.key() }),
    queryClient.invalidateQueries({ queryKey: ["membership/details"] }),
    queryClient.invalidateQueries({ queryKey: getPlatformLocations.key({}) }),
    // The product-listings key carries the platform and the unlocked ids but not the patient's
    // location, so every cached variant is stale.
    queryClient.invalidateQueries(getRetailproductsByPlatformIdProductListings.filters()),
    // The augmented catalogs live under their own keys, so invalidating the raw catalog above
    // does not touch them — without these the revalidator can hand back the previous location's
    // shop. Their by-id children are location-implicit and cached, so those go too.
    queryClient.invalidateQueries({ queryKey: ["shop/listings-with-unlocked"] }),
    queryClient.invalidateQueries({ queryKey: ["shop/memberships-with-unlocked"] }),
    queryClient.invalidateQueries({ queryKey: ["shop/unlocked-listings"] }),
    queryClient.invalidateQueries({ queryKey: ["shop/catalog-listings-verification"] }),
    queryClient.invalidateQueries(getListingsById.filters()),
    queryClient.invalidateQueries(getMembershipsByMembershipId.filters()),
  ]);

/**
 * Moves the patient to another of the platform's locations and re-scopes the shop to it.
 *
 * Shared by the shop's location banner and the "not sold at your location" screen a shared link
 * can land on, so both re-scope identically — a partial invalidation in one of them would leave
 * the patient looking at another location's catalog.
 */
export const useSwitchPlatformLocation = () => {
  const { revalidate } = useRevalidator();
  const [isSwitching, setIsSwitching] = useState(false);

  const switchLocation = async (platformLocationId: string): Promise<boolean> => {
    setIsSwitching(true);
    try {
      await postUserUpdate({ platformLocationId });
      await invalidateShopForLocationChange();
      // The shop index and home read the invalidated keys directly, so those refetch on their
      // own; the shop's other pages still take their catalog from a loader, which has to re-run
      // for the new location to take effect on screen.
      revalidate();
      return true;
    } catch {
      return false;
    } finally {
      setIsSwitching(false);
    }
  };

  return { switchLocation, isSwitching };
};
